import { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  XMarkIcon,
  CalendarIcon,
  ClockIcon,
  CubeIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import Button from "../../components/controls/Button";
import ServiceCalendar from "./ServiceCalendar";
import ServiceSlots from "./Serviceslots";

const toYmd = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const AddToCartServiceModal = ({ service, onClose }) => {
  const { t } = useTranslation();

  const priceTypeId = useMemo(
    () => parseInt(service?.priceTypeId ?? 0, 10),
    [service]
  );

  const [qty, setQty] = useState(1);
  const [proposeDates, setProposeDates] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    if (service?.id && (priceTypeId === 2 || priceTypeId === 3)) {
      fetchAvailableSlots();
    }
  }, [service?.id, priceTypeId]);

  const fetchAvailableSlots = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/inventory/services/${service.id}/available-slots?daysAhead=90`
      );
      if (response.ok) {
        const data = await response.json();
        setAvailableSlots(data || []);
      }
    } catch (error) {
      console.error("Erreur chargement créneaux:", error);
    } finally {
      setLoading(false);
    }
  };

  // Grouper les créneaux par date (attend date "YYYY-MM-DD" côté API; si ISO, on tranche)
  const slotsByDate = useMemo(() => {
    return (availableSlots || []).reduce((acc, slot) => {
      const dateKey = (slot?.date || "").slice(0, 10); // 'YYYY-MM-DD'
      if (!dateKey) return acc;
      if (!acc[dateKey]) acc[dateKey] = [];
      acc[dateKey].push(slot);
      return acc;
    }, {});
  }, [availableSlots]);

  // Slots pour la date sélectionnée
  const selectedDateSlots = useMemo(() => {
    if (!selectedDate) return [];
    return slotsByDate[selectedDate] || [];
  }, [selectedDate, slotsByDate]);

  const isFixed = priceTypeId === 1;
  const isHour = priceTypeId === 2;
  const isDay = priceTypeId === 3;
  const isUnit = priceTypeId === 4;

  const canConfirm = useMemo(() => {
    if (isFixed) return true;
    if (isUnit) return (qty || 0) > 0;

    if (isDay && proposeDates) return (qty || 0) > 0;
    if (isDay) return selectedSlot !== null && (qty || 0) > 0;

    if (isHour && proposeDates) return true;
    if (isHour) return selectedSlot !== null;

    return false;
  }, [isFixed, isUnit, isDay, isHour, qty, proposeDates, selectedSlot]);

  const confirm = () => {
    if (!canConfirm) return;
    const safeQty = Math.max(1, Number(qty || 1));

    const payload = {
      id: service.id,
      nameFr: service.nameFr,
      nameEn: service.nameEn,
      price: selectedSlot?.price || Number(service.price ?? 0),
      quantity: safeQty,
      priceTypeId,
      proposeDates,
      isWaitingForContact: proposeDates,
      // IMPORTANT: aucune ISO : on garde 'YYYY-MM-DD'
      eventDate: selectedSlot?.date
        ? String(selectedSlot.date).slice(0, 10)
        : null,
      startTime: selectedSlot?.startTime
        ? `${selectedSlot.startTime}:00`
        : null, // 'HH:MM:SS'
      endTime: selectedSlot?.endTime ? `${selectedSlot.endTime}:00` : null,
      companyId: service.companyId,
    };
    onClose?.(payload);
  };

  const previousMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1)
    );
    setSelectedDate(null);
    setSelectedSlot(null);
  };

  const nextMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1)
    );
    setSelectedDate(null);
    setSelectedSlot(null);
  };

  const PriceLabel = () => {
    const typeConfig = {
      1: { label: t("priceType.fixed", "fixe"), Icon: CubeIcon },
      2: { label: t("priceType.per_hour", "par heure"), Icon: ClockIcon },
      3: { label: t("priceType.per_day", "par journée"), Icon: CalendarIcon },
      4: { label: t("priceType.per_unit", "par unité"), Icon: CubeIcon },
    };

    const config = typeConfig[priceTypeId];
    const displayPrice = selectedSlot?.price ?? service?.price;
    const Icon = config?.Icon;

    if (displayPrice == null) return null;

    return (
      <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#FFD76A]/50 to-[#FFD76A]/60 px-4 py-2.5 rounded-xl shadow-md">
        {Icon && <Icon className="w-5 h-5 text-primary" />}
        <div>
          <span className="text-2xl font-bold text-primary">
            {Number(displayPrice).toFixed(2)} $
          </span>
          <span className="text-sm text-primary ml-1.5 font-medium">
            {config?.label}
          </span>
        </div>
      </div>
    );
  };

  const handleDateClick = (day) => {
    console.log("Date clicked:", day);
    if (day.hasSlots && day.isCurrentMonth) {
      setSelectedDate(day.dateStr); // 'YYYY-MM-DD'
      setSelectedSlot(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl overflow-hidden max-h-[90vh] flex flex-col animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="relative px-6 pt-6 pb-5 bg-gradient-to-br from-gray-50 to-white border-b">
          <button
            onClick={() => onClose?.(null)}
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
          >
            <XMarkIcon className="w-5 h-5 text-gray-500" />
          </button>

          <h3 className="text-2xl font-bold text-gray-900 pr-10 mb-2">
            {service?.nameFr || service?.nameEn}
          </h3>

          {service?.descriptionFr || service?.descriptionEn ? (
            <p className="text-gray-600 mb-3 leading-relaxed">
              {service?.descriptionFr || service?.descriptionEn}
            </p>
          ) : null}

          <PriceLabel />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {(isUnit || isDay) && (
            <div className="bg-gray-50 rounded-xl p-5 border border-gray-100">
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                {isUnit && t("global.quantity", "Quantité")}
                {isDay && t("global.days_count", "Nombre de jours")}
              </label>
              <input
                type="number"
                min={1}
                value={qty}
                onChange={(e) =>
                  setQty(Math.max(1, parseInt(e.target.value || "1", 10)))
                }
                className="w-full px-4 py-3 text-lg font-semibold border-2 border-gray-200 rounded-lg outline-none transition-all"
              />
            </div>
          )}

          {(isDay || isHour) && (
            <div className="bg-amber-50 rounded-xl p-5 border border-amber-100">
              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={proposeDates}
                  onChange={(e) => {
                    setProposeDates(e.target.checked);
                    if (e.target.checked) {
                      setSelectedSlot(null);
                      setSelectedDate(null);
                    }
                  }}
                  className="mt-0.5 w-5 h-5 rounded border-gray-300 text-primary focus:ring-2 focus:ring-primary focus:ring-offset-0 cursor-pointer"
                />
                <div className="flex-1">
                  <span className="font-medium text-gray-900 group-hover:text-gray-700 transition-colors">
                    {t(
                      "global.offer_availabilities",
                      "Me proposer des disponibilités"
                    )}
                  </span>
                  {proposeDates && (
                    <p className="text-sm text-amber-700 mt-1.5 flex items-start gap-2">
                      <InformationCircleIcon className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>
                        {t(
                          "global.provider_will_contact",
                          "Le fournisseur vous contactera pour déterminer une date."
                        )}
                      </span>
                    </p>
                  )}
                </div>
              </label>
            </div>
          )}

          {(isDay || isHour) && !proposeDates && (
            <div>
              {loading ? (
                <div className="text-center py-12">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#FFD76A] mb-4">
                    <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin"></div>
                  </div>
                  <p className="text-gray-600 font-medium">
                    {t(
                      "global.loading_slots",
                      "Chargement des disponibilités..."
                    )}
                  </p>
                </div>
              ) : Object.keys(slotsByDate).length === 0 ? (
                <div className="text-center py-12 bg-red-50 rounded-xl border border-red-100">
                  <ExclamationCircleIcon className="w-12 h-12 text-red-500 mx-auto mb-3" />
                  <p className="text-red-600 font-medium">
                    {t(
                      "global.no_availability",
                      "Aucune disponibilité présentement."
                    )}
                  </p>
                </div>
              ) : (
                <div className="space-y-5">
                  {/* Calendrier */}
                  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden max-w-md mx-auto">
                    <div className="bg-gradient-to-br from-[#FFD76A]/50 to-[#FFD76A]/60 px-3 py-2 border-b border-[#FFD76A]/30">
                      <div className="flex items-center justify-between">
                        <button
                          onClick={previousMonth}
                          className="p-1 hover:bg-white/50 rounded transition-colors"
                        >
                          <ChevronLeftIcon className="w-4 h-4 text-gray-700" />
                        </button>

                        <h4 className="text-xs font-bold text-gray-900">
                          {currentMonth.toLocaleDateString("fr-CA", {
                            month: "long",
                            year: "numeric",
                          })}
                        </h4>

                        <button
                          onClick={nextMonth}
                          className="p-1 hover:bg-white/50 rounded transition-colors"
                        >
                          <ChevronRightIcon className="w-4 h-4 text-gray-700" />
                        </button>
                      </div>
                    </div>

                    <ServiceCalendar
                      currentMonth={currentMonth}
                      slotsByDate={slotsByDate}
                      selectedDate={selectedDate}
                      onSelectDate={handleDateClick}
                    />
                  </div>

                  {/* Créneaux horaires */}
                  {selectedDate && selectedDateSlots.length > 0 && (
                    <ServiceSlots
                      selectedDate={selectedDate} // 'YYYY-MM-DD'
                      slots={selectedDateSlots}
                      selectedSlot={selectedSlot}
                      onSelectSlot={setSelectedSlot}
                      t={t}
                    />
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t flex gap-3 justify-end">
          <button
            className="px-6 py-2.5 rounded-lg font-medium text-gray-700 hover:bg-gray-100 transition-colors"
            onClick={() => onClose?.(null)}
          >
            {t("global.cancel", "Annuler")}
          </button>
          <Button
            color="primary"
            size="md"
            disabled={!canConfirm}
            onClick={confirm}
          >
            {isDay || isHour
              ? t("global.confirm_booking", "Confirmer la réservation")
              : t("global.add_to_cart", "Ajouter au panier")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddToCartServiceModal;
